import type { ButtonHTMLAttributes, ReactNode } from 'react'
import { cx } from '@/lib/cx'

export type ButtonVariant =
  | 'primary'
  | 'secondary'
  | 'destructive'
  | 'destructiveOutline'
  | 'outline'
  | 'ghost'

export type ButtonSize = 'small' | 'default' | 'large'

/** Shared with LinkButton, so a link that looks like a button cannot drift. */
export const BUTTON_VARIANTS: Record<ButtonVariant, string> = {
  primary:
    'border-transparent bg-accent text-on-accent hover:not-disabled:bg-accent-hover active:not-disabled:bg-accent-active',
  secondary:
    'bg-surface text-fg border-control hover:not-disabled:bg-hover active:not-disabled:bg-pressed',
  outline:
    'bg-surface text-accent border-accent-line hover:not-disabled:bg-accent-soft hover:not-disabled:border-accent',
  ghost: 'border-transparent text-fg-muted hover:not-disabled:bg-hover hover:not-disabled:text-fg',
  destructive: 'border-transparent bg-danger text-on-danger hover:not-disabled:bg-danger-hover',
  destructiveOutline:
    'bg-surface text-danger border-danger-line hover:not-disabled:bg-danger-soft hover:not-disabled:border-danger',
}

export const BUTTON_SIZES: Record<ButtonSize, string> = {
  // 36px box, 44px target: the card headers this sits in want a small button,
  // and a gloved hand still has to hit it. Needs 8px between adjacent buttons
  // so the invisible halves do not overlap.
  small: 'min-h-9 min-w-9 gap-1 px-3 text-xs after:absolute after:-inset-1 after:content-[""]',
  default: 'min-h-touch min-w-touch gap-2 px-4 text-sm',
  large: 'min-h-12 min-w-touch gap-2 px-5 text-base',
}

/** Every variant names its own border colour: leaving `border-transparent`
 *  here would beat a variant's own, because Tailwind orders utilities of the
 *  same family itself rather than by the order they are written in. */
export const BUTTON_BASE =
  'relative inline-flex items-center justify-center rounded-md border font-semibold leading-none whitespace-nowrap no-underline transition-colors duration-150 ease-standard disabled:cursor-not-allowed disabled:opacity-55'

interface Props extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant
  size?: ButtonSize
  block?: boolean
  loading?: boolean
  children: ReactNode
}

export function Button({
  variant = 'primary',
  size = 'default',
  block = false,
  loading = false,
  disabled,
  children,
  className,
  type = 'button',
  ...rest
}: Props) {
  return (
    <button
      type={type}
      className={cx(
        BUTTON_BASE,
        BUTTON_SIZES[size],
        BUTTON_VARIANTS[variant],
        block && 'w-full',
        className,
      )}
      // Only an in-flight action disables the button. Never disable it to
      // express a validation error — let it be pressed and say what is wrong.
      disabled={disabled === true || loading}
      aria-busy={loading || undefined}
      {...rest}
    >
      {loading && (
        <span
          aria-hidden="true"
          className="size-4 animate-spin rounded-full border-2 border-current border-t-transparent"
        />
      )}
      {children}
    </button>
  )
}
